import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../services/api";
import { customerLabel } from "./CustomerSelect";
import ImportExportBar from "./ImportExportBar";

const emptyForm = { beatName: "", beatCode: "", salesPersonId: "", territory: "", active: true };
const visitDays = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const listStyle = { maxHeight: "30vh", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" };
const rowStyle = { display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderBottom: "1px solid #f1f1f1" };

const Beats = () => {
  const [beats, setBeats] = useState([]);
  const [salesPeople, setSalesPeople] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [assignBeat, setAssignBeat] = useState(null);
  const [assignedCustomers, setAssignedCustomers] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  // assigned customers the user unticked (to be removed) and unassigned customers the user ticked (to be added)
  const [toRemove, setToRemove] = useState(new Set());
  const [toAdd, setToAdd] = useState(new Set());
  const [search, setSearch] = useState("");
  const [visitDay, setVisitDay] = useState("MON");
  const [assigning, setAssigning] = useState(false);

  const loadBeats = async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/v1/beats", { size: 500 });
      setBeats(data?.content || []);
    } catch (error) {
      console.error("Failed to load beats", error);
      toast.error(error.message || "Failed to load beats");
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.getAll("/api/v1/customers");
      setCustomers(data?.content || []);
    } catch (error) {
      console.error("Failed to load customers", error);
      toast.error(error.message || "Failed to load customers");
    }
  };

  const loadSalesmen = async () => {
    try {
      // sales persons of type SALESMAN (one per salesman user), already limited to this distributor's active users
      const data = await api.get("/api/v1/sales-persons", { type: "SALESMAN" });
      setSalesmen(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load salesmen", error);
      toast.error(error.message || "Failed to load salesmen");
    }
  };

  useEffect(() => {
    loadBeats();
    loadCustomers();
    loadSalesmen();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (beat) => {
    setEditingId(beat.id);
    setFormData({
      beatName: beat.beatName || "",
      beatCode: beat.beatCode || "",
      salesPersonId: beat.salesPersonId ?? "",
      territory: beat.territory || "",
      active: beat.active ?? true,
    });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        beatName: formData.beatName,
        beatCode: formData.beatCode.trim() || null,
        // the dropdown shows names; its value is the sales person id stored in beats.sales_person_id
        salesPersonId: formData.salesPersonId ? Number(formData.salesPersonId) : null,
        territory: formData.territory || null,
        active: formData.active,
      };
      if (editingId) {
        await api.put(`/api/v1/beats/${editingId}`, payload);
        toast.success("Beat updated");
      } else {
        await api.post("/api/v1/beats", payload);
        toast.success("Beat created");
      }
      setShowModal(false);
      loadBeats();
    } catch (error) {
      console.error("Failed to save beat", error);
      toast.error(error.message || "Failed to save beat");
    } finally {
      setSaving(false);
    }
  };

  const openAssign = async (beat) => {
    setAssignBeat(beat);
    setToRemove(new Set());
    setToAdd(new Set());
    setSearch("");
    setVisitDay("MON");
    setAssignLoading(true);
    try {
      const data = await api.get(`/api/v1/beats/${beat.id}/customers`);
      setAssignedCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load assigned customers", error);
      toast.error(error.message || "Failed to load assigned customers");
    } finally {
      setAssignLoading(false);
    }
  };

  const toggleIn = (setter) => (id) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleRemove = toggleIn(setToRemove);
  const toggleAdd = toggleIn(setToAdd);

  const customerById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);
  const matches = (name) => !search.trim() || (name || "").toLowerCase().includes(search.trim().toLowerCase());
  const assignedIds = new Set(assignedCustomers.map((ac) => ac.customerId));
  // unassigned = customers on no beat at all
  const unassignedCustomers = customers.filter((c) => c.beatId == null && !assignedIds.has(c.id) && c.active !== false);

  const handleSaveAssignments = async () => {
    if (toAdd.size === 0 && toRemove.size === 0) {
      toast.info("No changes to save");
      return;
    }
    setAssigning(true);
    try {
      if (toRemove.size > 0) {
        await api.post(`/api/v1/beats/${assignBeat.id}/customers/unassign`, { customerIds: Array.from(toRemove) });
      }
      if (toAdd.size > 0) {
        await api.post(`/api/v1/beats/${assignBeat.id}/customers/bulk-assign`, { customerIds: Array.from(toAdd), visitDay });
      }
      toast.success(`Beat updated: ${toAdd.size} added, ${toRemove.size} removed`);
      await loadCustomers();
      openAssign(assignBeat);
      // Refresh so each customer's beatId reflects the move — otherwise the "currently on another beat"
      // warning stays stale until a full page reload.
      loadCustomers();
    } catch (error) {
      console.error("Failed to update beat customers", error);
      toast.error(error.message || "Failed to update beat customers");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading beats...</div>
          </div>
        </div>
      ) : null}

      <div className="filters-bar">
        <button className="primary" onClick={openCreate}>
          + New Beat
        </button>
        <ImportExportBar
          importPath="/api/v1/beats/import"
          exportPath="/api/v1/beats/export"
          exportFilename="beats.xlsx"
          label="Beats"
          onImported={loadBeats}
        />
      </div>
      <div className="page-subtitle" style={{ margin: "4px 0 10px" }}>
        Excel import columns: <b>Beat Name</b> (required), Beat Code, Territory, Active. A beat name can be used only once.
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Code</th>
              <th>Territory</th>
              <th>Salesman</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {beats.length === 0 ? (
              <tr>
                <td colSpan={7}>No beats found.</td>
              </tr>
            ) : (
              beats.map((beat) => (
                <tr key={beat.id}>
                  <td>{beat.id}</td>
                  <td>{beat.beatName}</td>
                  <td>{beat.beatCode || "-"}</td>
                  <td>{beat.territory || "-"}</td>
                  <td>{beat.salesmanName || "-"}</td>
                  <td>
                    <span className={`badge ${beat.active ? "badge-green" : "badge-gray"}`}>
                      {beat.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="action-buttons flex gap-2">
                    <button className="secondary" onClick={() => openEdit(beat)}>
                      Edit
                    </button>
                    <button className="secondary" onClick={() => openAssign(beat)}>
                      Assign Customers
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">{editingId ? "Edit Beat" : "Create Beat"}</h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                Close
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Beat Name</label>
                  <input name="beatName" value={formData.beatName} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Beat Code (optional)</label>
                  <input name="beatCode" value={formData.beatCode} onChange={handleChange} maxLength={50} />
                </div>
                <div className="form-group">
                  <label>Territory</label>
                  <input name="territory" value={formData.territory} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Salesman (optional)</label>
                  <select name="salesPersonId" value={formData.salesPersonId} onChange={handleChange}>
                    <option value="">No salesman</option>
                    {salesmen.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.userName}
                        {s.userPhone ? ` (${s.userPhone})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Active</label>
                  <select
                    name="active"
                    value={formData.active ? "true" : "false"}
                    onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.value === "true" }))}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {assignBeat ? (
        <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && setAssignBeat(null)}>
          <div className="modal-box" style={{ maxWidth: "720px" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Assign Customers — {assignBeat.beatName}</h3>
              <button className="close-btn" onClick={() => setAssignBeat(null)}>
                Close
              </button>
            </div>

            <div className="form-group" style={{ marginBottom: "10px" }}>
              <input placeholder="Search customers" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>

            <h4 style={{ margin: "8px 0" }}>
              Assigned customers ({assignedCustomers.length}) — untick to remove from this beat
            </h4>
            <div style={listStyle}>
              {assignLoading ? (
                <div style={rowStyle}>Loading...</div>
              ) : assignedCustomers.length === 0 ? (
                <div style={rowStyle}>No customers assigned yet.</div>
              ) : (
                assignedCustomers
                  .filter((ac) => matches(customerLabel(customerById.get(ac.customerId))))
                  .map((ac) => (
                    <label key={ac.customerId} style={{ ...rowStyle, ...(toRemove.has(ac.customerId) ? { opacity: 0.5 } : {}) }}>
                      <input type="checkbox" checked={!toRemove.has(ac.customerId)} onChange={() => toggleRemove(ac.customerId)} />
                      <span>
                        {customerLabel(customerById.get(ac.customerId)) || `Customer ${ac.customerId}`} ({ac.visitDay})
                      </span>
                    </label>
                  ))
              )}
            </div>

            <h4 style={{ margin: "16px 0 8px" }}>Unassigned customers ({unassignedCustomers.length}) — tick to add</h4>
            <div className="form-group" style={{ marginBottom: "8px" }}>
              <label>Visit day for added customers</label>
              <select value={visitDay} onChange={(e) => setVisitDay(e.target.value)}>
                {visitDays.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div style={listStyle}>
              {unassignedCustomers.length === 0 ? (
                <div style={rowStyle}>Every customer is already on a beat.</div>
              ) : (
                unassignedCustomers
                  .filter((c) => matches(customerLabel(c)))
                  .map((customer) => (
                    <label key={customer.id} style={rowStyle}>
                      <input type="checkbox" checked={toAdd.has(customer.id)} onChange={() => toggleAdd(customer.id)} />
                      <span>{customerLabel(customer)}</span>
                    </label>
                  ))
              )}
            </div>

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={() => setAssignBeat(null)}>
                Close
              </button>
              <button type="button" className="primary" onClick={handleSaveAssignments} disabled={assigning}>
                {assigning ? "Saving..." : `Save (${toAdd.size} to add, ${toRemove.size} to remove)`}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Beats;
